import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  onValue,
  push,
  ref as databaseRef,
  runTransaction,
  set,
} from "firebase/database";
import {
  Camera,
  ChevronLeft,
  ChevronRight,
  Crown,
  Heart,
  RefreshCw,
  X,
} from "lucide-react";
import {
  ensureAnonymousUser,
  firebaseDatabase,
  isFirebaseSecurityConfigured,
} from "../../firebase/client";
import { type WeddingLanguage, weddingContent } from "./content";
import { ImageWithFallback } from "./ImageWithFallback";

interface GuestCameraPhotosProps {
  language: WeddingLanguage;
}

interface GuestPhoto {
  id: string;
  storagePath: string;
  downloadUrl: string;
  cloudinaryPublicId: string;
  status: "approved";
  createdAt: number;
  approvedAt: number;
  source: "camera";
  ownerUid: string;
  likes: number;
}

declare global {
  interface Window {
    __weddingFailedGuestPhotos?: Array<{
      id: string;
      downloadUrl: string;
      storagePath: string;
      cloudinaryPublicId: string;
      likes: number;
      createdAt: string;
    }>;
  }
}

type CameraFacingMode = "environment" | "user";

const IMAGE_QUALITY = 1;
const cloudinaryCloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const cloudinaryUploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const isCloudinaryConfigured = Boolean(
  cloudinaryCloudName && cloudinaryUploadPreset,
);

const normalizeGuestPhoto = (id: string, value: any): GuestPhoto | null => {
  if (
    !value ||
    value.status !== "approved" ||
    value.source !== "camera" ||
    typeof value.downloadUrl !== "string"
  ) {
    return null;
  }

  return {
    id,
    storagePath: String(value.storagePath || ""),
    downloadUrl: value.downloadUrl,
    cloudinaryPublicId: String(value.cloudinaryPublicId || ""),
    status: "approved",
    createdAt: Number(value.createdAt) || 0,
    approvedAt: Number(value.approvedAt) || 0,
    source: "camera",
    ownerUid: String(value.ownerUid || ""),
    likes: Number.isFinite(Number(value.likes)) ? Number(value.likes) : 0,
  };
};

const orderGuestPhotos = (items: GuestPhoto[]) => {
  const topPhotos = [...items]
    .filter((photo) => photo.likes > 0)
    .sort((left, right) => {
      if (right.likes !== left.likes) {
        return right.likes - left.likes;
      }

      return right.createdAt - left.createdAt;
    })
    .slice(0, 3);
  const topPhotoIds = new Set(topPhotos.map((photo) => photo.id));
  const rest = [...items]
    .filter((photo) => !topPhotoIds.has(photo.id))
    .sort((left, right) => right.createdAt - left.createdAt);

  return [...topPhotos, ...rest];
};

const stopMediaStream = (stream: MediaStream | null) => {
  stream?.getTracks().forEach((track) => track.stop());
};

const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not capture photo."));
          return;
        }

        resolve(blob);
      },
      "image/jpeg",
      IMAGE_QUALITY,
    );
  });

const uploadPhotoToCloudinary = async (blob: Blob) => {
  if (!cloudinaryCloudName || !cloudinaryUploadPreset) {
    throw new Error("Cloudinary is not configured.");
  }

  const formData = new FormData();
  formData.append("file", blob, "wedding-camera-photo.jpg");
  formData.append("upload_preset", cloudinaryUploadPreset);
  formData.append("tags", "wedding,camera");
  formData.append("context", "source=camera");

  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${cloudinaryCloudName}/image/upload`,
    {
      method: "POST",
      body: formData,
    },
  );

  if (!response.ok) {
    throw new Error("Cloudinary upload failed.");
  }

  const result = await response.json();

  if (
    typeof result.secure_url !== "string" ||
    typeof result.public_id !== "string"
  ) {
    throw new Error("Cloudinary upload response is invalid.");
  }

  return {
    downloadUrl: result.secure_url as string,
    publicId: result.public_id as string,
  };
};

const formatPhotoDate = (createdAt: number, language: WeddingLanguage) => {
  if (!createdAt) {
    return "";
  }

  return new Intl.DateTimeFormat(language === "vi" ? "vi-VN" : "en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(createdAt));
};

export function GuestCameraPhotos({ language }: GuestCameraPhotosProps) {
  const content = weddingContent[language].guestPhotos;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const successTimeoutRef = useRef<number | null>(null);

  const [photos, setPhotos] = useState<GuestPhoto[]>([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraFacingMode, setCameraFacingMode] =
    useState<CameraFacingMode>("environment");
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [slideshowIndex, setSlideshowIndex] = useState<number | null>(null);
  const [failedPhotoIds, setFailedPhotoIds] = useState<Record<string, boolean>>(
    {},
  );
  const [failedPhotoDetails, setFailedPhotoDetails] = useState<
    Record<string, GuestPhoto>
  >({});
  const [likeCooldowns, setLikeCooldowns] = useState<Record<string, boolean>>(
    {},
  );
  const visiblePhotos = photos.filter((photo) => !failedPhotoIds[photo.id]);
  const hasVisiblePhotos = visiblePhotos.length > 0;

  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }

    setPreviewUrl("");
    setCapturedBlob(null);
  }, []);

  const clearSuccessTimeout = useCallback(() => {
    if (successTimeoutRef.current) {
      window.clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
  }, []);

  const closeCamera = useCallback(() => {
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    setCameraOpen(false);
  }, []);

  useEffect(() => {
    if (!isFirebaseSecurityConfigured || !firebaseDatabase) {
      setRequestError(content.appCheckError);
      return undefined;
    }

    const photosRef = databaseRef(firebaseDatabase, "weddingPhotos");
    const unsubscribe = onValue(
      photosRef,
      (snapshot) => {
        const value = snapshot.val();

        if (!value) {
          setPhotos([]);
          setRequestError("");
          return;
        }

        const nextPhotos = Object.entries(value)
          .map(([id, photo]) => normalizeGuestPhoto(id, photo))
          .filter((photo): photo is GuestPhoto => Boolean(photo));

        const orderedPhotos = orderGuestPhotos(nextPhotos);

        setPhotos(orderedPhotos);
        setRequestError("");
      },
      () => {
        setRequestError(content.loadError);
      },
    );

    return unsubscribe;
  }, [content.appCheckError, content.loadError]);

  useEffect(
    () => () => {
      clearSuccessTimeout();
      closeCamera();
      clearPreview();
    },
    [clearPreview, clearSuccessTimeout, closeCamera],
  );

  useEffect(() => {
    if (
      slideshowIndex !== null &&
      (!visiblePhotos.length || slideshowIndex >= visiblePhotos.length)
    ) {
      setSlideshowIndex(null);
    }
  }, [slideshowIndex, visiblePhotos.length]);

  useEffect(() => {
    const currentPhotoIds = new Set(photos.map((photo) => photo.id));

    setFailedPhotoIds((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([photoId]) => currentPhotoIds.has(photoId)),
      ),
    );
    setFailedPhotoDetails((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([photoId]) => currentPhotoIds.has(photoId)),
      ),
    );
  }, [photos]);

  useEffect(() => {
    const failedPhotos = Object.values(failedPhotoDetails);

    if (!failedPhotos.length) {
      window.__weddingFailedGuestPhotos = [];
      return;
    }

    window.__weddingFailedGuestPhotos = failedPhotos.map((photo) => ({
      id: photo.id,
      downloadUrl: photo.downloadUrl,
      storagePath: photo.storagePath,
      cloudinaryPublicId: photo.cloudinaryPublicId,
      likes: photo.likes,
      createdAt: photo.createdAt ? new Date(photo.createdAt).toISOString() : "",
    }));
    console.clear();
    console.info(
      `[Wedding guest photos] Failed image records to delete: ${failedPhotos.length}`,
    );
    console.table(window.__weddingFailedGuestPhotos);
    console.info("Copy with: JSON.stringify(window.__weddingFailedGuestPhotos, null, 2)");
  }, [failedPhotoDetails]);

  useEffect(() => {
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraOpen]);

  useEffect(() => {
    if (slideshowIndex === null || visiblePhotos.length < 2) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setSlideshowIndex((currentIndex) => {
        if (currentIndex === null) {
          return currentIndex;
        }

        return (currentIndex + 1) % visiblePhotos.length;
      });
    }, 5200);

    return () => window.clearInterval(intervalId);
  }, [slideshowIndex, visiblePhotos.length]);

  useEffect(() => {
    if (slideshowIndex === null) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSlideshowIndex(null);
      }

      if (event.key === "ArrowRight") {
        setSlideshowIndex((currentIndex) =>
          currentIndex === null || !visiblePhotos.length
            ? currentIndex
            : (currentIndex + 1) % visiblePhotos.length,
        );
      }

      if (event.key === "ArrowLeft") {
        setSlideshowIndex((currentIndex) =>
          currentIndex === null || !visiblePhotos.length
            ? currentIndex
            : (currentIndex - 1 + visiblePhotos.length) % visiblePhotos.length,
        );
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [slideshowIndex, visiblePhotos.length]);

  const startCamera = async (facingMode: CameraFacingMode) => {
    setCameraError("");
    setSuccessMessage("");

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(content.cameraUnavailable);
      return;
    }

    try {
      stopMediaStream(streamRef.current);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: facingMode },
        },
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      setCameraFacingMode(facingMode);
      setCameraOpen(true);
    } catch (_error) {
      setCameraError(content.cameraPermissionError);
    }
  };

  const openCamera = async () => {
    clearPreview();
    startCamera(cameraFacingMode);
  };

  const switchCamera = () => {
    const nextFacingMode =
      cameraFacingMode === "environment" ? "user" : "environment";

    startCamera(nextFacingMode);
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      setCameraError(content.captureError);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext("2d");

    if (!context) {
      setCameraError(content.captureError);
      return;
    }

    if (cameraFacingMode === "user") {
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    if (cameraFacingMode === "user") {
      context.setTransform(1, 0, 0, 1, 0, 0);
    }

    try {
      const blob = await canvasToBlob(canvas);

      clearPreview();
      const nextPreviewUrl = URL.createObjectURL(blob);
      previewUrlRef.current = nextPreviewUrl;
      setCapturedBlob(blob);
      setPreviewUrl(nextPreviewUrl);
      closeCamera();
    } catch (_error) {
      setCameraError(content.captureError);
    }
  };

  const sendPhoto = async () => {
    if (!capturedBlob) {
      return;
    }

    try {
      setLoading(true);
      setCameraError("");
      setRequestError("");
      setSuccessMessage("");

      if (
        !isFirebaseSecurityConfigured ||
        !isCloudinaryConfigured ||
        !firebaseDatabase
      ) {
        throw new Error("Firebase security is not configured.");
      }

      const user = await ensureAnonymousUser();
      const photoRef = push(databaseRef(firebaseDatabase, "weddingPhotos"));
      const photoId = photoRef.key;

      if (!photoId) {
        throw new Error("Could not create photo id.");
      }

      const cloudinaryPhoto = await uploadPhotoToCloudinary(capturedBlob);
      const now = Date.now();

      await set(photoRef, {
        id: photoId,
        storagePath: cloudinaryPhoto.publicId,
        downloadUrl: cloudinaryPhoto.downloadUrl,
        cloudinaryPublicId: cloudinaryPhoto.publicId,
        status: "approved",
        createdAt: now,
        approvedAt: now,
        source: "camera",
        ownerUid: user.uid,
        likes: 0,
      });

      clearPreview();
      setSuccessMessage(content.sendSuccess);
      clearSuccessTimeout();
      successTimeoutRef.current = window.setTimeout(() => {
        setSuccessMessage("");
        successTimeoutRef.current = null;
      }, 5000);
    } catch (_error) {
      setRequestError(content.sendError);
    } finally {
      setLoading(false);
    }
  };

  const retakePhoto = () => {
    clearPreview();
    openCamera();
  };

  const showPreviousPhoto = () => {
    setSlideshowIndex((currentIndex) =>
      currentIndex === null || !visiblePhotos.length
        ? currentIndex
        : (currentIndex - 1 + visiblePhotos.length) % visiblePhotos.length,
    );
  };

  const showNextPhoto = () => {
    setSlideshowIndex((currentIndex) =>
      currentIndex === null || !visiblePhotos.length
        ? currentIndex
        : (currentIndex + 1) % visiblePhotos.length,
    );
  };

  const likePhoto = async (photoId: string) => {
    if (
      !isFirebaseSecurityConfigured ||
      !firebaseDatabase ||
      !photoId ||
      likeCooldowns[photoId]
    ) {
      if (!isFirebaseSecurityConfigured || !firebaseDatabase) {
        setRequestError(content.appCheckError);
      }

      return;
    }

    setLikeCooldowns((prev) => ({ ...prev, [photoId]: true }));
    setPhotos((prev) =>
      orderGuestPhotos(
        prev.map((photo) =>
          photo.id === photoId ? { ...photo, likes: photo.likes + 1 } : photo,
        ),
      ),
    );

    try {
      await ensureAnonymousUser();
      const result = await runTransaction(
        databaseRef(firebaseDatabase, `weddingPhotos/${photoId}/likes`),
        (currentLikes) => {
          const safeLikes =
            Number.isFinite(Number(currentLikes)) && currentLikes !== null
              ? Number(currentLikes)
              : 0;
          return safeLikes + 1;
        },
      );

      if (!result.committed) {
        throw new Error("Photo like was not committed.");
      }

      setRequestError("");
    } catch (_error) {
      setRequestError("We couldn't like this photo right now.");
      setPhotos((prev) =>
        orderGuestPhotos(
          prev.map((photo) =>
            photo.id === photoId
              ? { ...photo, likes: Math.max(0, photo.likes - 1) }
              : photo,
          ),
        ),
      );
    } finally {
      window.setTimeout(() => {
        setLikeCooldowns((prev) => {
          const next = { ...prev };
          delete next[photoId];
          return next;
        });
      }, 1200);
    }
  };

  const handleLikePhoto = (
    event: React.MouseEvent<HTMLButtonElement>,
    photoId: string,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    likePhoto(photoId);
  };

  const handleGuestPhotoImageError = (photo: GuestPhoto) => {
    setFailedPhotoIds((prev) => {
      if (prev[photo.id]) {
        return prev;
      }

      return { ...prev, [photo.id]: true };
    });
    setFailedPhotoDetails((prev) => {
      if (prev[photo.id]) {
        return prev;
      }

      return { ...prev, [photo.id]: photo };
    });
  };

  const slideshowPhoto =
    slideshowIndex === null ? null : visiblePhotos[slideshowIndex] || null;
  const slideshowPhotoDate = slideshowPhoto
    ? formatPhotoDate(slideshowPhoto.createdAt, language)
    : "";
  const topPhotoRanks = visiblePhotos
    .filter((photo) => photo.likes > 0)
    .sort((left, right) => {
      if (right.likes !== left.likes) {
        return right.likes - left.likes;
      }

      return right.createdAt - left.createdAt;
    })
    .slice(0, 3)
    .reduce<Record<string, number>>((accumulator, photo, index) => {
      accumulator[photo.id] = index + 1;
      return accumulator;
    }, {});

  return (
    <section id="guest-photos" className="wedding-section wedding-section--beige">
      <div className="wedding-container">
        <div className="wedding-header">
          <h2 className="wedding-title">
            {language === "en" ? (
              <><span className="guest-camera__heading-label">GUEST CAMERA</span>{" "}<span className="guest-camera__heading-script">Moments</span></>
            ) : content.heading}
          </h2>
          <p className="guest-camera__intro">{content.intro}</p>
          <div className="wedding-divider" />
        </div>

        <div className="guest-camera">
          <div className="guest-camera__capture">
            <div className="guest-camera__surface">
              {cameraOpen && (
                <button
                  type="button"
                  className="guest-camera__switch"
                  onClick={switchCamera}
                  aria-label={language === "vi" ? "Đổi camera" : "Switch camera"}
                  title={language === "vi" ? "Đổi camera" : "Switch camera"}
                >
                  <RefreshCw size={18} strokeWidth={1.8} />
                </button>
              )}

              {cameraOpen && (
                <video
                  ref={videoRef}
                  className={`guest-camera__video ${
                    cameraFacingMode === "user"
                      ? "guest-camera__video--mirrored"
                      : ""
                  }`}
                  autoPlay
                  playsInline
                  muted
                />
              )}

              {!cameraOpen && previewUrl && (
                <img
                  src={previewUrl}
                  alt={content.previewAlt}
                  className="guest-camera__preview"
                />
              )}

              {!cameraOpen && !previewUrl && (
                <div className="guest-camera__placeholder">
                  <span aria-hidden="true">
                    <Camera size={26} strokeWidth={1.5} />
                  </span>
                  <p>{content.placeholder}</p>
                </div>
              )}
            </div>

            <canvas ref={canvasRef} className="guest-camera__canvas" />

            <div className="guest-camera__actions">
              {!cameraOpen && !previewUrl && (
                <button
                  type="button"
                  className="wedding-button guest-camera__button"
                  onClick={openCamera}
                >
                  {content.openCamera}
                </button>
              )}

              {cameraOpen && (
                <>
                  <button
                    type="button"
                    className="wedding-button guest-camera__button"
                    onClick={capturePhoto}
                  >
                    {content.takePhoto}
                  </button>
                  <button
                    type="button"
                    className="guest-camera__secondary-button"
                    onClick={closeCamera}
                  >
                    {content.closeCamera}
                  </button>
                </>
              )}

              {!cameraOpen && previewUrl && (
                <>
                  <button
                    type="button"
                    className="wedding-button guest-camera__button"
                    onClick={sendPhoto}
                    disabled={loading}
                  >
                    {loading ? content.sending : content.sendPhoto}
                  </button>
                  <button
                    type="button"
                    className="guest-camera__secondary-button"
                    onClick={retakePhoto}
                    disabled={loading}
                  >
                    {content.retake}
                  </button>
                </>
              )}
            </div>

            {cameraError && <p className="guest-camera__error">{cameraError}</p>}
            {requestError && <p className="guest-camera__error">{requestError}</p>}
            {successMessage && (
              <p className="guest-camera__success">{successMessage}</p>
            )}
          </div>

          <div className="guest-camera__gallery">
            <p className="guest-camera__vote-prompt">{content.votePrompt}</p>
            {hasVisiblePhotos ? (
              <div className="guest-photo-grid">
                {visiblePhotos.map((photo, index) => (
                  <div
                    className={`guest-photo-card ${
                      topPhotoRanks[photo.id] ? "guest-photo-card--top" : ""
                    }`}
                    key={photo.id}
                  >
                    <button
                      type="button"
                      className="guest-photo-card__open"
                      onClick={() => setSlideshowIndex(index)}
                      aria-label={`${language === "vi" ? "Mở" : "Open"} ${content.guestPhotoAlt}`}
                    >
                      {topPhotoRanks[photo.id] && (
                        <span
                          className={`guest-photo-card__rank guest-photo-card__rank--${topPhotoRanks[photo.id]}`}
                          aria-label={language === "vi" ? `Ảnh được yêu thích thứ ${topPhotoRanks[photo.id]}` : `Top ${topPhotoRanks[photo.id]} most liked photo`}
                          title={language === "vi" ? `Ảnh được yêu thích thứ ${topPhotoRanks[photo.id]}` : `Top ${topPhotoRanks[photo.id]} most liked photo`}
                        >
                          <Crown size={17} strokeWidth={2} />
                        </span>
                      )}
                      <ImageWithFallback
                        src={photo.downloadUrl}
                        alt={content.guestPhotoAlt}
                        className="guest-photo-card__image"
                        onError={() => handleGuestPhotoImageError(photo)}
                      />
                    </button>
                    <span className="guest-photo-card__bar">
                      <button
                        type="button"
                        className="guest-photo-card__like"
                        onClick={(event) => handleLikePhoto(event, photo.id)}
                        disabled={Boolean(likeCooldowns[photo.id])}
                        aria-label={language === "vi" ? "Thả tim ảnh" : "Like photo"}
                        title={language === "vi" ? "Thả tim ảnh" : "Like photo"}
                      >
                        <Heart size={15} strokeWidth={1.8} />
                        <span>{photo.likes}</span>
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="guest-camera__empty">{content.empty}</div>
            )}
          </div>
        </div>
      </div>

      {slideshowPhoto && (
        <div
          className="guest-photo-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={content.guestPhotoAlt}
        >
          <button
            type="button"
            className="guest-photo-lightbox__backdrop"
            onClick={() => setSlideshowIndex(null)}
            aria-label={language === "vi" ? "Đóng ảnh" : "Close photo"}
          />
          <div className="guest-photo-lightbox__stage">
            <button
              type="button"
              className="guest-photo-lightbox__close"
              onClick={() => setSlideshowIndex(null)}
              aria-label={language === "vi" ? "Đóng ảnh" : "Close photo"}
            >
              <X size={22} strokeWidth={1.75} />
            </button>
            {visiblePhotos.length > 1 && (
              <button
                type="button"
                className="guest-photo-lightbox__nav guest-photo-lightbox__nav--previous"
                onClick={showPreviousPhoto}
                aria-label={language === "vi" ? "Ảnh trước" : "Previous photo"}
              >
                <ChevronLeft size={30} strokeWidth={1.5} />
              </button>
            )}
            <img
              key={slideshowPhoto.id}
              src={slideshowPhoto.downloadUrl}
              alt={content.guestPhotoAlt}
              className="guest-photo-lightbox__image"
            />
            {visiblePhotos.length > 1 && (
              <button
                type="button"
                className="guest-photo-lightbox__nav guest-photo-lightbox__nav--next"
                onClick={showNextPhoto}
                aria-label={language === "vi" ? "Ảnh tiếp theo" : "Next photo"}
              >
                <ChevronRight size={30} strokeWidth={1.5} />
              </button>
            )}
            <div className="guest-photo-lightbox__meta">
              <span className="guest-photo-lightbox__info">
                {slideshowPhotoDate && (
                  <span className="guest-photo-lightbox__date">
                    {slideshowPhotoDate}
                  </span>
                )}
                <span className="guest-photo-lightbox__count">
                  <span>
                    {String((slideshowIndex || 0) + 1).padStart(2, "0")}
                  </span>
                  <span
                    aria-hidden="true"
                    className="guest-photo-lightbox__divider"
                  />
                  <span>{String(visiblePhotos.length).padStart(2, "0")}</span>
                </span>
              </span>
              <button
                type="button"
                className="guest-photo-card__like guest-photo-lightbox__like"
                onClick={() => likePhoto(slideshowPhoto.id)}
                disabled={Boolean(likeCooldowns[slideshowPhoto.id])}
                aria-label={language === "vi" ? "Thả tim ảnh" : "Like photo"}
                title={language === "vi" ? "Thả tim ảnh" : "Like photo"}
              >
                <Heart size={15} strokeWidth={1.8} />
                <span>{slideshowPhoto.likes}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
